/**
 * 联机对战协调器（设计文档 §9/§10 Phase 3）。
 *
 * 职责：把「大厅 → 房间 → 对局 → 结算 → 再来一局/返回」的界面流转、
 * NetClient 网络会话、TankGame 的 PvP 对局三者接起来。
 * - UI（UIController）只负责渲染与回调，不碰网络；
 * - TankGame 只负责模拟与发送，接收由本协调器订阅 NetClient 后转发；
 * - 本类不碰游戏模拟细节，只做状态机与消息路由。
 */

import { NetClient } from '../net/NetClient.js';
import type { WelcomePayload } from '../net/NetClient.js';
import type { MatchResult, PvpMode, RoomSnapshot } from '../net/protocol.js';
import type { PvpContext } from './types.js';
import type { TankGame } from './TankGame.js';
import type { OnlineUIActions, UIController } from '../ui/UIController.js';

/** 协调器所处的联机流程阶段。 */
type OnlineStage = 'idle' | 'lobby' | 'room' | 'playing' | 'settlement';

/** 刷新重连用会话（sessionStorage；标签页关闭即失效）。 */
interface PvpSession {
  code: string;
  playerId: string;
  nick: string;
  at: number;
}

const NICK_STORAGE_KEY = 'PVP_NICK';
const SESSION_STORAGE_KEY = 'PVP_SESSION';
/** 会话恢复有效期（超过就认为房间早已被回收，直接进大厅）。 */
const SESSION_MAX_AGE_MS = 10 * 60 * 1000;

export class OnlinePvP {
  private readonly ui: UIController;
  private readonly game: TankGame;
  private net: NetClient | null = null;
  private room: RoomSnapshot | null = null;
  private stage: OnlineStage = 'idle';
  /** 建房/进房请求进行中（防连点）。 */
  private busy = false;
  /** 最近一次使用的昵称（大厅输入框回填）。 */
  private nick = '';

  constructor(ui: UIController, game: TankGame) {
    this.ui = ui;
    this.game = game;
    this.nick = this.readStorage(NICK_STORAGE_KEY, 'local') ?? '';
    this.ui.setOnlineActions(this.buildActions());
    // 对局中「返回主菜单」（ESC 暂停屏）统一走这里：leave_room 触发服务端判负后再断连
    this.game.pvpQuitHandler = () => this.exitToMenu();
    this.tryRestoreSession();
  }

  // -------------------------------------------------------------------------
  // UI 动作（OnlineUIActions 契约，UIController 菜单/大厅/房间/结算屏回调）
  // -------------------------------------------------------------------------

  private buildActions(): OnlineUIActions {
    return {
      onEnterLobby: () => {
        this.stage = 'lobby';
        this.ui.showLobby(this.nick);
        this.refreshRoomList();
      },
      onCreateRoom: (mode: PvpMode, nick: string) => {
        void this.connectAndEnter(() => NetClient.createRoom(mode, nick), nick);
      },
      onJoinRoom: (code: string, nick: string) => {
        void this.connectAndEnter(() => NetClient.joinRoom(code, nick), nick);
      },
      onLeaveLobby: () => {
        this.stage = 'idle';
        this.ui.showMenu();
      },
      onRefreshRooms: () => {
        this.refreshRoomList();
      },
      onSetReady: (ready: boolean) => {
        this.net?.setReady(ready);
      },
      onStartMatch: () => {
        this.net?.startGame();
      },
      onLeaveRoom: () => {
        this.teardownNet();
        this.stage = 'lobby';
        this.ui.showLobby(this.nick);
        this.refreshRoomList();
      },
      onRematch: () => {
        // 结算屏 → 房间屏：清掉对局现场（quitToMenu 会复位 PvP 状态并显示主菜单），
        // 再立刻切回房间屏等待下次 match_start。
        this.game.quitToMenu();
        if (this.net && this.room) {
          this.stage = 'room';
          this.ui.showRoom(this.room, this.net.playerId ?? '');
        } else {
          // 连接已断：回大厅重建。
          this.stage = 'lobby';
          this.ui.showLobby(this.nick);
        }
      },
      onExitToMenu: () => {
        this.exitToMenu();
      },
    };
  }

  // -------------------------------------------------------------------------
  // 连接与房间
  // -------------------------------------------------------------------------

  /** 建房/进房统一入口：连接成功 → 房间屏；失败 → 留在大厅并提示。 */
  private async connectAndEnter(dial: () => Promise<NetClient>, nick: string): Promise<void> {
    if (this.busy) return;
    const trimmed = nick.trim();
    if (!trimmed) {
      this.ui.toast('✎', '需要昵称', '先给自己起个名字再开战');
      return;
    }
    this.busy = true;
    this.nick = trimmed;
    this.writeStorage(NICK_STORAGE_KEY, trimmed, 'local');
    try {
      const client = await dial();
      this.attachNet(client, client.lastWelcome);
    } catch (error) {
      const message = error instanceof Error ? error.message : '连接失败';
      this.ui.toast('✕', '无法进入房间', message);
      // 进房失败（满员/已开赛/不存在）：列表可能已过期，顺手刷新
      if (this.stage === 'lobby') this.refreshRoomList();
    } finally {
      this.busy = false;
    }
  }

  /** 拉取并渲染大厅房间列表（仅大厅阶段可见；失败降级为列表内提示）。 */
  private refreshRoomList(): void {
    if (this.stage !== 'lobby') return;
    this.ui.renderRoomList(null);
    NetClient.listRooms()
      .then((rooms) => {
        if (this.stage === 'lobby') this.ui.renderRoomList(rooms);
      })
      .catch(() => {
        if (this.stage === 'lobby') this.ui.renderRoomList(null, '房间列表加载失败，可稍后用右上角按钮刷新');
      });
  }

  /** 连接成功后：保存会话、订阅事件、切到房间屏。 */
  private attachNet(client: NetClient, welcome: WelcomePayload | null): void {
    this.teardownNet();
    this.net = client;
    const room = welcome?.room ?? null;
    this.room = room;
    if (room && client.playerId) {
      this.saveSession({ code: room.code, playerId: client.playerId, nick: this.nick, at: Date.now() });
    }

    client.on('room_update', (next) => {
      this.room = next;
      // playing / settlement 阶段房间屏不可见，全量刷新只会把结算屏切走——
      // 此时只缓存，成员连接态变化由 TankGame 的 player_left 与播报覆盖。
      if (this.stage === 'room' && this.net) {
        this.ui.showRoom(next, this.net.playerId ?? '');
      }
    });
    client.on('match_start', ({ startsAt, room: startRoom }) => {
      if (!this.net) return;
      const selfId = this.net.playerId ?? '';
      const self = startRoom.members.find((member) => member.playerId === selfId);
      if (!self) return;
      this.room = startRoom;
      const context: PvpContext = {
        roomCode: startRoom.code,
        mode: startRoom.mode,
        selfId,
        selfTeam: self.team,
        members: startRoom.members,
      };
      this.stage = 'playing';
      this.ui.hideOnlineScreens();
      void this.game.startPvpGame(context, this.net, startsAt);
    });
    client.on('relay', ({ from, snapshot }) => this.game.handlePvpRelay(from, snapshot));
    client.on('relay_event', ({ from, event }) => this.game.handlePvpEvent(from, event));
    client.on('player_left', ({ playerId }) => {
      this.game.handlePvpPlayerLeft(playerId);
      // 对局中补一条具名 toast。注意 room_update 通常已先把该成员移出快照，
      // 这里只能在还能找到名字时提示，找不到就静默跳过。
      if (this.stage === 'playing' || this.stage === 'settlement') {
        const name = this.room?.members.find((member) => member.playerId === playerId)?.name;
        if (name) this.ui.toast('⚑', '玩家离开', `${name} 离开了对战`);
      }
    });
    client.on('match_end', (result) => this.onMatchEnd(result));
    client.on('error', ({ message }) => {
      this.ui.toast('✕', '联机错误', message);
    });
    client.on('disconnected', () => this.onDisconnected());

    this.stage = 'room';
    if (room) this.ui.showRoom(room, client.playerId ?? '');
  }

  /** match_end → 结算（TankGame 负责演出与结算屏，这里只管阶段与比分缓存）。 */
  private onMatchEnd(result: MatchResult): void {
    if (this.stage !== 'playing') return;
    this.stage = 'settlement';
    this.game.handlePvpMatchEnd(result);
  }

  /** 退出到主菜单：对局中先清本地对局，再 leave_room（服务端据此判 forfeit）并断连。 */
  private exitToMenu(): void {
    if (this.stage === 'playing' || this.stage === 'settlement') this.game.quitToMenu();
    this.teardownNet();
    this.stage = 'idle';
    this.ui.showMenu();
  }

  /** 非预期断线：清理并回大厅。 */
  private onDisconnected(): void {
    const wasInMatch = this.stage === 'playing' || this.stage === 'settlement';
    if (wasInMatch) this.game.quitToMenu();
    this.teardownNet();
    this.stage = 'lobby';
    this.ui.toast('⛓', '连接已断开', '已返回联机大厅，可重新进入房间');
    this.ui.showLobby(this.nick);
    this.refreshRoomList();
  }

  /** 主动离开/收尾：通知服务端、断开连接、清会话。 */
  private teardownNet(): void {
    if (this.net) {
      this.net.leaveRoom();
      this.net.disconnect();
      this.net = null;
    }
    this.room = null;
    this.clearSession();
  }

  // -------------------------------------------------------------------------
  // 刷新重连（sessionStorage 会话恢复；2026-07-26 起对局中退出立即判负、席位不保留，
  // 恢复只在大厅/房间阶段有意义——对局已结束则会落到 waiting 房间或回大厅）
  // -------------------------------------------------------------------------

  private tryRestoreSession(): void {
    const raw = this.readStorage(SESSION_STORAGE_KEY, 'session');
    if (!raw) return;
    let session: PvpSession | null = null;
    try {
      session = JSON.parse(raw) as PvpSession;
    } catch {
      session = null;
    }
    if (!session || Date.now() - session.at > SESSION_MAX_AGE_MS) {
      this.clearSession();
      return;
    }
    const { code, playerId, nick } = session;
    this.nick = nick;
    const client = new NetClient();
    client
      .connect(code, nick, playerId)
      .then((welcome) => {
        this.attachNet(client, welcome);
        this.ui.toast('⟳', '已恢复联机会话', welcome.room.phase === 'playing' ? '正在回到对局' : '已回到房间');
      })
      .catch(() => {
        client.disconnect();
        this.clearSession();
      });
  }

  private saveSession(session: PvpSession): void {
    this.writeStorage(SESSION_STORAGE_KEY, JSON.stringify(session), 'session');
  }

  private clearSession(): void {
    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      /* 隐私模式忽略 */
    }
  }

  // -------------------------------------------------------------------------
  // 存储小工具（隐私模式 try/catch）
  // -------------------------------------------------------------------------

  private readStorage(key: string, kind: 'local' | 'session'): string | null {
    try {
      return (kind === 'local' ? window.localStorage : window.sessionStorage).getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string, kind: 'local' | 'session'): void {
    try {
      (kind === 'local' ? window.localStorage : window.sessionStorage).setItem(key, value);
    } catch {
      /* 隐私模式忽略 */
    }
  }
}
